import MyLocationIcon from '@mui/icons-material/MyLocation'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import {
  APIProvider,
  Map as GoogleMap,
  MapControl,
  ControlPosition,
  useMap,
  useAdvancedMarkerRef,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Pin,
} from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import React, { useMemo, useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react'

import MapPlaceholder from 'components/common/address/MapPlaceholder'
import { GOOGLE_MAPS_MAX_ZOOM } from 'components/common/map/constants'
import MapStyleThumbnail from 'components/common/map/MapStyleThumbnail'
import MarkerOffsetControl from 'components/common/map/MarkerOffsetControl'
import { nudgeDeckOverlay } from 'components/common/map/nudgeDeckOverlay'
import AdaptiveContainerMarkerLayer from 'components/common/map/webgl/containers/AdaptiveContainerMarkerLayer'
import { applyFeatureOffset } from 'components/route-manager/RouteManagerMapV2/utils/applyFeatureOffset'
import { calculateCollisionOffsets } from 'components/route-manager/RouteManagerMapV2/utils/calculateCollisionOffsets'
import Constants from 'Constants'
import { isValidLatitudeRange, isValidLongitudeRange } from 'utils/validations'

const { GOOGLE_MAPS_API_KEY } = Constants

const DEFAULT_ZOOM = 20
const CAMERA_MOVED_THRESHOLD = 0.0001
const LOCATION_PIN_Z_INDEX = 1000
// Spiderfy radius (meters) for containers sharing a geopoint — same default as the inventory map
const CONTAINER_MARKER_OFFSET_METERS = 4

const MAP_OPTIONS = {
  disableDefaultUI: true,
  streetViewControl: false,
  mapId: 'map-view-v2',
  gestureHandling: 'greedy',
  mapTypeControl: false,
  zoomControl: true,
  fullscreenControl: false,
  scaleControl: false,
}

const TopRightControls = ({ lat, lng, showReset, onReset }) => {
  const map = useMap()

  const handleCenterOnMarker = useCallback(() => {
    if (map) {
      map.panTo({ lat, lng })
      map.setZoom(DEFAULT_ZOOM)
    }
  }, [map, lat, lng])

  return (
    <MapControl position={ControlPosition.TOP_RIGHT}>
      <Box alignItems="center" display="flex" gap={1} mr={2} mt={2}>
        {showReset && (
          <Button
            color="error"
            size="small"
            sx={{ backgroundColor: 'background.paper', '&:hover': { backgroundColor: 'background.paper' } }}
            onClick={onReset}
          >
            Reset
          </Button>
        )}
        <Card>
          <Tooltip title="Center map on marker">
            <IconButton size="small" sx={{ p: 0.75 }} onClick={handleCenterOnMarker}>
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Card>
      </Box>
    </MapControl>
  )
}

TopRightControls.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  showReset: PropTypes.bool.isRequired,
  onReset: PropTypes.func.isRequired,
}

const DraggableLocationMarker = React.memo(({ latitude, longitude, onDragEnd }) => {
  const [markerRef, marker] = useAdvancedMarkerRef()

  const handleDragEnd = useCallback(
    e => {
      if (!e?.latLng) return
      const mapLat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat
      const mapLng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng
      onDragEnd({ mapLng, mapLat })
    },
    [onDragEnd]
  )

  // Set marker.position synchronously before paint so Google Maps settles the
  // position before usePropBinding's useEffect fires after paint.
  useLayoutEffect(() => {
    if (!marker) return
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      marker.position = { lat: latitude, lng: longitude }
    }
  }, [marker, latitude, longitude])

  return (
    <AdvancedMarker
      ref={markerRef}
      draggable
      anchorPoint={AdvancedMarkerAnchorPoint.BOTTOM}
      position={{ lat: latitude, lng: longitude }}
      zIndex={LOCATION_PIN_Z_INDEX}
      onDragEnd={handleDragEnd}
    >
      <Pin />
    </AdvancedMarker>
  )
})

DraggableLocationMarker.displayName = 'DraggableLocationMarker'

DraggableLocationMarker.propTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  onDragEnd: PropTypes.func.isRequired,
}

// Lives inside <GoogleMap> for useMap(): nudges deck.gl awake when the feature set
// changes so the WebGL overlay redraws — same pattern as the inventory containers map.
const MapDeckNudge = ({ featureSignature }) => {
  const map = useMap()
  useEffect(() => {
    if (!map) return undefined
    return nudgeDeckOverlay(map)
  }, [map, featureSignature])
  return null
}

MapDeckNudge.propTypes = {
  featureSignature: PropTypes.string,
}

// Fits the camera to coordinate changes that did NOT originate from a map
// gesture (pin drag / map click) — i.e. typed lat/long inputs and address
// autocomplete. Self-initiated moves are flagged via `selfMoveRef` by the
// gesture handlers and skipped here so drags don't yank the camera. Unlike a
// plain pan, this fits BOUNDS around the new pin position plus its containers
// so the full new location is framed; `features` is read via a ref so the
// effect only fires on the external lat/lng change, not on container/offset
// tweaks (MapFitAllPins owns container-set refits).
const MapFitOnExternalChange = ({ lat, lng, features, selfMoveRef }) => {
  const map = useMap()
  const featuresRef = useRef(features)
  featuresRef.current = features
  useEffect(() => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng) || !window.google?.maps) return
    const self = selfMoveRef.current
    // eslint-disable-next-line no-param-reassign
    selfMoveRef.current = null
    if (self && Math.abs(self.lat - lat) < 1e-9 && Math.abs(self.lng - lng) < 1e-9) return
    const bounds = new window.google.maps.LatLngBounds()
    bounds.extend({ lat, lng })
    featuresRef.current.forEach(feature => {
      const [featureLng, featureLat] = feature.geometry.coordinates
      bounds.extend({ lat: featureLat, lng: featureLng })
    })
    map.fitBounds(bounds, 100)
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > DEFAULT_ZOOM) map.setZoom(DEFAULT_ZOOM)
    })
  }, [map, lat, lng, selfMoveRef])
  return null
}

MapFitOnExternalChange.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  features: PropTypes.array.isRequired,
  selfMoveRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
}

// Fits the camera around the location pin plus every container pin when the container
// set changes (not on pin drags), then caps the zoom so tight clusters stay readable.
const MapFitAllPins = ({ lat, lng, features, featureSignature }) => {
  const map = useMap()
  useEffect(() => {
    if (!map || features.length === 0 || !window.google?.maps) return
    const bounds = new window.google.maps.LatLngBounds()
    bounds.extend({ lat, lng })
    features.forEach(feature => {
      const [featureLng, featureLat] = feature.geometry.coordinates
      bounds.extend({ lat: featureLat, lng: featureLng })
    })
    map.fitBounds(bounds, 100)
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > DEFAULT_ZOOM) map.setZoom(DEFAULT_ZOOM)
    })
    // Refit only when the container set changes — lat/lng updates during drags must not yank the camera
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, featureSignature])
  return null
}

MapFitAllPins.propTypes = {
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  features: PropTypes.array.isRequired,
  featureSignature: PropTypes.string.isRequired,
}

const MapViewV2 = ({
  longitude = 0,
  longitudeForMapHistory,
  latitude = 0,
  latitudeForMapHistory,
  containers = [],
  previewContainersAtPin = false,
  onChange,
  onReset,
}) => {
  const lat = parseFloat(latitude)
  const lng = parseFloat(longitude)
  const isValidLatLong = isValidLatitudeRange(lat) && isValidLongitudeRange(lng)

  // Coordinates the user just set via a map gesture — consumed by
  // MapPanOnExternalChange to tell input-driven changes apart from drags.
  const selfMoveRef = useRef(null)
  const handleSelfInitiatedChange = useCallback(
    ({ mapLng, mapLat }) => {
      selfMoveRef.current = { lat: mapLat, lng: mapLng }
      onChange({ mapLng, mapLat })
    },
    [onChange]
  )

  const mapCenter = useMemo(() => ({ lat: isValidLatLong ? lat : 0, lng: isValidLatLong ? lng : 0 }), [lat, lng, isValidLatLong])

  const [mapType, setMapType] = useState('satellite')
  const [isCameraMoved, setIsCameraMoved] = useState(false)
  const [containerOffset, setContainerOffset] = useState(CONTAINER_MARKER_OFFSET_METERS)
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)

  const handleContainerOffsetChange = useCallback(value => setContainerOffset(value), [])

  const hasHistory = isValidLatitudeRange(parseFloat(latitudeForMapHistory)) && isValidLongitudeRange(parseFloat(longitudeForMapHistory))
  const isPinMoved = hasHistory && (lat !== parseFloat(latitudeForMapHistory) || lng !== parseFloat(longitudeForMapHistory))

  // GeoJSON features for the shared adaptive (DOM/WebGL) container marker layer, spiderfied
  // like the inventory map. The location pin joins the offset calc as a phantom member so
  // containers sharing its geopoint (the common case) ring out around it instead of under it.
  const containerFeatures = useMemo(() => {
    // Preview mode (move-containers checkbox + pending geo change): show every
    // container at the pin's new position; the spiderfy below rings them out.
    // Containers with no real coords yet (0,0 placeholders) are included too —
    // saving positions them at the new location, so the preview should match.
    const previewAtPin = previewContainersAtPin && isValidLatLong
    const features = containers
      .filter(container => {
        if (previewAtPin) return true
        const containerLat = parseFloat(container.latitude)
        const containerLng = parseFloat(container.longitude)
        const isOriginPlaceholder = containerLat === 0 && containerLng === 0
        return isValidLatitudeRange(containerLat) && isValidLongitudeRange(containerLng) && !isOriginPlaceholder
      })
      .map(container => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: previewAtPin ? [lng, lat] : [parseFloat(container.longitude), parseFloat(container.latitude)],
        },
        properties: container,
      }))
    if (features.length === 0 || containerOffset <= 0) return features

    const phantomLocationPin = isValidLatLong
      ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties: { id: 'location-pin' } }]
      : []
    const offsetMap = calculateCollisionOffsets([...features, ...phantomLocationPin], { zoom, baseOffset: containerOffset })
    return features.map(feature => applyFeatureOffset(feature, offsetMap))
  }, [containers, containerOffset, isValidLatLong, lat, lng, previewContainersAtPin, zoom])

  // Identity of the container set — drives the deck nudge and the fit-bounds (offset/drag tweaks excluded)
  const containerSignature = useMemo(() => containerFeatures.map(feature => feature.properties.id).join('|'), [containerFeatures])

  const handleBasemapToggle = useCallback(() => setMapType(prev => (prev === 'satellite' ? 'roadmap' : 'satellite')), [])

  const handleCameraChanged = useCallback(
    ev => {
      const { center } = ev.detail
      const moved = Math.abs(center.lat - lat) > CAMERA_MOVED_THRESHOLD || Math.abs(center.lng - lng) > CAMERA_MOVED_THRESHOLD
      setIsCameraMoved(moved)
      const nextZoom = Math.round(ev.detail.zoom)
      setZoom(prev => (prev === nextZoom ? prev : nextZoom))
    },
    [lat, lng]
  )

  const handleMapClick = useCallback(
    ev => {
      if (ev.detail.latLng) {
        const newLat = ev.detail.latLng.lat
        const newLng = ev.detail.latLng.lng
        handleSelfInitiatedChange({ mapLng: newLng, mapLat: newLat })
      }
    },
    [handleSelfInitiatedChange]
  )

  useEffect(() => {
    setIsCameraMoved(false)
  }, [lat, lng])

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Box height="100%" position="relative" width="100%">
        {isValidLatLong ? (
          <GoogleMap
            defaultCenter={mapCenter}
            defaultZoom={DEFAULT_ZOOM}
            gridSize={null}
            mapTypeId={mapType}
            maxZoom={GOOGLE_MAPS_MAX_ZOOM}
            options={MAP_OPTIONS}
            onCameraChanged={handleCameraChanged}
            onClick={handleMapClick}
          >
            <MapControl position={ControlPosition.TOP_LEFT}>
              <Box ml={2} mt={2}>
                <MapStyleThumbnail baseStyle="roadmap" isSatellite={mapType === 'satellite'} onToggle={handleBasemapToggle} />
              </Box>
            </MapControl>

            {(isCameraMoved || isPinMoved) && <TopRightControls lat={lat} lng={lng} showReset={isPinMoved} onReset={onReset} />}

            {containerFeatures.length > 0 && (
              <MapControl position={ControlPosition.LEFT_BOTTOM}>
                <Box mb={3} ml={1}>
                  <MarkerOffsetControl max={30} value={containerOffset} onChange={handleContainerOffsetChange} />
                </Box>
              </MapControl>
            )}

            <AdaptiveContainerMarkerLayer features={containerFeatures} />
            <MapDeckNudge featureSignature={`${containerSignature}|${previewContainersAtPin}`} />
            <MapFitAllPins features={containerFeatures} featureSignature={containerSignature} lat={lat} lng={lng} />
            <MapFitOnExternalChange features={containerFeatures} lat={lat} lng={lng} selfMoveRef={selfMoveRef} />

            <DraggableLocationMarker latitude={lat} longitude={lng} onDragEnd={handleSelfInitiatedChange} />
          </GoogleMap>
        ) : (
          <MapPlaceholder />
        )}
      </Box>
    </APIProvider>
  )
}

MapViewV2.propTypes = {
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitudeForMapHistory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  latitudeForMapHistory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  // Preview the move-containers checkbox: render containers at the pin's position.
  previewContainersAtPin: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
}

export default MapViewV2
